import { ReloadOutlined } from '@ant-design/icons';
import {
  EInvoiceType,
  EOrderEventActorType,
  EOrderEventType,
  EOrderHistoryCategory,
  EOrderHistoryEntryKind,
  type IOrderAddressChange,
  type IOrderHistoryActor,
  type IOrderHistoryEmailOpens,
  ORDER_NOTE_MAX_LENGTH,
  type TOrderEventPayload,
  type TOrderHistoryEntry,
} from '@mahawi/eshop-common/src/types';
import {
  Button,
  Empty,
  Form,
  Input,
  Select,
  Skeleton,
  Space,
  Spin,
  Table,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../../fragments/date-format';
import {
  describeOrderHistoryActor,
  formatEmailTypeCode,
  ORDER_EVENT_TYPE_LABEL,
  ORDER_HISTORY_CATEGORY_COLOR,
  ORDER_HISTORY_CATEGORY_LABEL,
} from '../../fragments/order-history-labels';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import { orderHistoryLoad, orderNoteAdd } from '../../reducers/order/reducer';
import { type IOrderHistoryState } from '../../reducers/order/types';
import { SPACING } from '../../theme/tokens';
import Link from '../link';
import RelativeTime from '../relative-time';
import StatusTag from '../status-tag';
import { WAREHOUSE_ORDER_PAIRING_SOURCE_LABEL } from '../warehouse-order/labels';

const { Paragraph, Text } = Typography;

interface INoteForm {
  text: string;
}

const TIMELINE_TITLE_SPAN = '10rem';

const formatDateTime = (value: string): string =>
  dayjs(value).format(EDateFormat.DATE_TIME);

const formatOpens = (opens: IOrderHistoryEmailOpens): string =>
  opens.count === 1
    ? `Opened once, ${formatDateTime(opens.firstAt)}`
    : `Opened ${opens.count}×, first ${formatDateTime(opens.firstAt)}, last ${formatDateTime(opens.lastAt)}`;

const addressChangeColumns = [
  { title: 'Field', dataIndex: 'field', key: 'field' },
  {
    title: 'Before',
    dataIndex: 'from',
    key: 'from',
    render: (value: string | null): string => value ?? '—',
  },
  {
    title: 'After',
    dataIndex: 'to',
    key: 'to',
    render: (value: string | null): string => value ?? '—',
  },
];

function describeEvent(event: TOrderEventPayload): React.ReactNode {
  const label: string = ORDER_EVENT_TYPE_LABEL[event.type];

  switch (event.type) {
    case EOrderEventType.STATUS_CHANGED:
      return (
        <Space size={SPACING.xs} wrap>
          <Text>{label}</Text>
          {event.data.fromStatus && (
            <StatusTag status={event.data.fromStatus} />
          )}
          <Text>→</Text>
          <StatusTag status={event.data.toStatus} />
        </Space>
      );
    case EOrderEventType.TAG_ADDED:
    case EOrderEventType.TAG_REMOVED:
      return `${label}: ${event.data.tagName}`;
    case EOrderEventType.ADDRESS_CHANGED:
      return (
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Text>{`${label} (${event.data.addressType.toLowerCase()})`}</Text>
          <Table<IOrderAddressChange>
            size="small"
            pagination={false}
            rowKey="field"
            columns={addressChangeColumns}
            dataSource={event.data.changes}
          />
        </Space>
      );
    case EOrderEventType.NOTE_ADDED:
      return (
        <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
          {event.data.text}
        </Paragraph>
      );
    case EOrderEventType.INVOICE_REQUESTED:
      return `${label}: ${event.data.fakturoidNumber}`;
    case EOrderEventType.FAKTUROID_CONTACT_SYNCED:
      return `${label} (${event.data.operation})`;
    case EOrderEventType.PACKET_CREATED:
      return `${label} (${event.data.carrierVendorCode})`;
    case EOrderEventType.PACKET_ADDED_MANUALLY:
      return `${label} (${event.data.carrierVendorCode}, ${event.data.trackingNumber})`;
    case EOrderEventType.PACKET_STATUS_SYNC_REQUESTED:
      return `${label} (${event.data.packetThirdPartyId})`;
    case EOrderEventType.ATTACHMENT_VISIBILITY_CHANGED:
      return `${label}: ${event.data.filename} is now ${event.data.isPublic ? 'public' : 'private'}`;
    case EOrderEventType.SHIPPED_EMAIL_SENT:
    case EOrderEventType.SHIPPING_TIME_EMAIL_SENT:
    case EOrderEventType.REVIEW_REQUEST_SENT:
      return `${label} to ${event.data.recipients.join(', ')}`;
    case EOrderEventType.VAT_ID_VERIFIED:
      return `${label}: ${event.data.vatId} is ${
        event.data.isValid === null
          ? 'not verifiable now'
          : event.data.isValid
            ? 'valid'
            : 'not valid'
      }`;
    case EOrderEventType.WAREHOUSE_ORDER_PLACED:
      return `${label}: ${event.data.warehouseNames.join(', ')}`;
    case EOrderEventType.WAREHOUSE_ORDER_PAIRED:
      return `${label}: ${event.data.warehouseName} ${event.data.warehouseOrderCode} (${WAREHOUSE_ORDER_PAIRING_SOURCE_LABEL[event.data.source].toLowerCase()})`;
    case EOrderEventType.WAREHOUSE_ORDER_UNPAIRED:
      return `${label}: ${event.data.warehouseName} ${event.data.warehouseOrderCode}`;
    case EOrderEventType.REVIEW_STATUS_CHANGED:
      return (
        <Space size={SPACING.xs} wrap>
          <Text>{label}</Text>
          <StatusTag status={event.data.toStatus} />
          {event.data.rejectionReason && (
            <Text type="secondary">{event.data.rejectionReason}</Text>
          )}
        </Space>
      );
  }
}

export function describeOrderHistoryEntry(
  entry: TOrderHistoryEntry,
): React.ReactNode {
  switch (entry.kind) {
    case EOrderHistoryEntryKind.ORDER_CREATED:
      return (
        <Space size={SPACING.xs}>
          <Text>Order created</Text>
          <StatusTag status={entry.detail.orderStatus} />
        </Space>
      );
    case EOrderHistoryEntryKind.ORDER_EVENT:
      return describeEvent(entry.detail);
    case EOrderHistoryEntryKind.EMAIL_SENT:
      return (
        <Space orientation="vertical" size={SPACING.none}>
          <Text>{`${formatEmailTypeCode(entry.detail.emailTypeCode)} email sent to ${entry.detail.recipients.join(', ')}`}</Text>
          <Text type="secondary">
            {entry.detail.opens
              ? formatOpens(entry.detail.opens)
              : 'Not opened'}
          </Text>
        </Space>
      );
    case EOrderHistoryEntryKind.EMAIL_OPENED:
      return (
        <Space orientation="vertical" size={SPACING.none}>
          <Text>{`${formatEmailTypeCode(entry.detail.emailTypeCode)} email opened`}</Text>
          <Text type="secondary">{formatOpens(entry.detail.opens)}</Text>
        </Space>
      );
    case EOrderHistoryEntryKind.PAYMENT_CREATED:
      return `Payment created (${entry.detail.vendor}, ${entry.detail.thirdPartyPaymentId})`;
    case EOrderHistoryEntryKind.PAYMENT_STATUS:
      return (
        <Space size={SPACING.xs}>
          <Text>{`Payment ${entry.detail.thirdPartyPaymentId}`}</Text>
          <StatusTag status={entry.detail.status} />
        </Space>
      );
    case EOrderHistoryEntryKind.INVOICE_CREATED:
      return `${entry.detail.invoiceType === EInvoiceType.INVOICE ? 'Invoice' : 'Credit note'} ${entry.detail.fakturoidNumber} recorded`;
    case EOrderHistoryEntryKind.PACKET_REGISTERED:
      return `Packet ${entry.detail.barcode ?? entry.detail.thirdPartyId} registered${
        entry.detail.carrierServiceName
          ? ` (${entry.detail.carrierServiceName})`
          : ''
      }`;
    case EOrderHistoryEntryKind.PACKET_STATUS:
      return (
        <Space size={SPACING.xs} wrap>
          <Text>{`Packet ${entry.detail.thirdPartyId}`}</Text>
          <StatusTag status={entry.detail.status} />
          {entry.detail.carrierStatus && (
            <Text type="secondary">{entry.detail.carrierStatus}</Text>
          )}
          {entry.detail.note && (
            <Text type="secondary">{entry.detail.note}</Text>
          )}
        </Space>
      );
    case EOrderHistoryEntryKind.ATTACHMENT_ADDED:
      return `Attachment ${entry.detail.filename} added (${entry.detail.isPublic ? 'public' : 'private'})`;
    case EOrderHistoryEntryKind.WITHDRAWAL_RECEIVED:
      return `Withdrawal received (${entry.detail.channel})`;
    case EOrderHistoryEntryKind.WITHDRAWAL_STATUS_CHANGED:
      return (
        <Space size={SPACING.xs} wrap>
          <Text>Withdrawal status</Text>
          {entry.detail.fromStatus && (
            <StatusTag status={entry.detail.fromStatus} />
          )}
          <Text>→</Text>
          <StatusTag status={entry.detail.toStatus} />
          {entry.detail.refundedAmount !== null && (
            <Text type="secondary">{`refunded ${entry.detail.refundedAmount}`}</Text>
          )}
        </Space>
      );
  }
}

function OrderHistoryActor({
  actor,
}: {
  actor: IOrderHistoryActor;
}): React.ReactElement {
  const label: string = describeOrderHistoryActor(actor);

  if (actor.actorType === EOrderEventActorType.STAFF && actor.accountUuid) {
    return <Link to={`/account/${actor.accountUuid}`} label={label} />;
  }

  return <Text type="secondary">{label}</Text>;
}

const categoryOptions = Object.values(EOrderHistoryCategory).map(
  (category: EOrderHistoryCategory) => ({
    label: ORDER_HISTORY_CATEGORY_LABEL[category],
    value: category,
  }),
);

function OrderHistory({
  orderUUID,
}: {
  orderUUID: string;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<INoteForm>();
  const history: IOrderHistoryState = useSelector(
    (state: RootState) => state.Order.history,
  );
  const [categories, setCategories] = useState<EOrderHistoryCategory[]>([]);
  const isNoteSubmitted = useRef<boolean>(false);

  useEffect(() => {
    if (isNoteSubmitted.current) {
      isNoteSubmitted.current = false;
      form.resetFields();
    }
  }, [history.loadedAt, form]);

  const isCurrentOrder: boolean = history.orderUUID === orderUUID;

  const visibleEntries: TOrderHistoryEntry[] = useMemo(
    () =>
      isCurrentOrder
        ? history.entries.filter(
            (entry: TOrderHistoryEntry): boolean =>
              categories.length === 0 || categories.includes(entry.category),
          )
        : [],
    [categories, history.entries, isCurrentOrder],
  );

  const onAddNote = (values: INoteForm): void => {
    isNoteSubmitted.current = true;
    dispatch(orderNoteAdd(orderUUID, values.text));
  };

  if (!isCurrentOrder || (history.loadedAt === 0 && history.inProcess)) {
    return <Skeleton active />;
  }

  return (
    <Space orientation="vertical" size={SPACING.lg} style={{ width: '100%' }}>
      <Form<INoteForm> form={form} layout="vertical" onFinish={onAddNote}>
        <Form.Item
          name="text"
          label="Internal note"
          rules={[
            { required: true, whitespace: true, message: 'Write a note.' },
            {
              max: ORDER_NOTE_MAX_LENGTH,
              message: `Use at most ${ORDER_NOTE_MAX_LENGTH} characters.`,
            },
          ]}
        >
          <Input.TextArea
            rows={2}
            maxLength={ORDER_NOTE_MAX_LENGTH}
            showCount
            disabled={history.noteInProcess}
            placeholder="Visible to staff only"
          />
        </Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          disabled={history.noteInProcess}
        >
          Add note
        </Button>
      </Form>

      <Space size={SPACING.lg} align="center">
        <Select<EOrderHistoryCategory[]>
          mode="multiple"
          allowClear
          placeholder="All categories"
          options={categoryOptions}
          value={categories}
          onChange={setCategories}
          style={{ minWidth: 280 }}
        />
        <Tooltip title="Refresh history">
          <Button
            aria-label="Refresh history"
            onClick={(): void => {
              dispatch(orderHistoryLoad(orderUUID));
            }}
          >
            <ReloadOutlined />
          </Button>
        </Tooltip>
        <Spin size="small" spinning={history.inProcess} />
      </Space>

      {visibleEntries.length === 0 ? (
        <Empty description="No history entries" />
      ) : (
        <Timeline
          mode="start"
          titleSpan={TIMELINE_TITLE_SPAN}
          items={visibleEntries.map((entry: TOrderHistoryEntry) => ({
            key: entry.id,
            color: ORDER_HISTORY_CATEGORY_COLOR[entry.category],
            title: (
              <Space size={SPACING.xs} wrap>
                <RelativeTime value={entry.occurredAt} />
                {entry.actor && <OrderHistoryActor actor={entry.actor} />}
              </Space>
            ),
            content: describeOrderHistoryEntry(entry),
          }))}
        />
      )}
    </Space>
  );
}

export default OrderHistory;
